import React, { useEffect, useRef, useState } from "react";
import YouTube from "react-youtube";

const SpeechRecognition =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition || window.mozSpeechRecognition || null
    : null;

const extractYouTubeID = (value) => {
  if (!value) return "";
  const match = value.match(
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
  );
  return match ? match[1] : "";
};

const TranscriptionTool = () => {
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState("");
  const [mediaType, setMediaType] = useState("audio");
  const [uploadedMediaName, setUploadedMediaName] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeVideoId, setYoutubeVideoId] = useState("");
  const [transcript, setTranscript] = useState("");
  const [status, setStatus] = useState("Ready to transcribe.");
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const transcriptRef = useRef("");
  const listeningRef = useRef(false);
  const mediaRef = useRef(null);

  useEffect(() => {
    if (!SpeechRecognition) {
      setStatus("Speech recognition is not supported in this browser.");
      return undefined;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      let interim = "";
      let finalText = transcriptRef.current;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += `${result} `;
        } else {
          interim += result;
        }
      }
      transcriptRef.current = finalText;
      setTranscript(`${finalText}${interim}`);
      setStatus("Listening… keep your audio audible.");
    };
    recognition.onerror = (event) => {
      setStatus(`Recognition error: ${event.error}`);
    };
    recognition.onend = async () => {
      if (!listeningRef.current) {
        setListening(false);
        setStatus("Transcription paused.");
        return;
      }
      try {
        await recognition.start();
        setStatus("Continuing transcription…");
      } catch (error) {
        console.warn("Resuming transcription failed", error);
        listeningRef.current = false;
        setListening(false);
        setStatus("Transcription paused.");
      }
    };
    recognitionRef.current = recognition;
    return () => {
      recognition.stop();
    };
  }, []);

  useEffect(() => {
    const id = extractYouTubeID(youtubeUrl.trim());
    if (id) {
      setYoutubeVideoId(id);
    } else {
      setYoutubeVideoId("");
    }
  }, [youtubeUrl]);

  const handleMediaUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadedMediaName(file.name);
    setMediaType(file.type.startsWith("video") ? "video" : "audio");
    if (mediaPreviewUrl) {
      URL.revokeObjectURL(mediaPreviewUrl);
    }
    const url = URL.createObjectURL(file);
    setMediaPreviewUrl(url);
    setStatus(`Loaded ${file.name}. Start transcription when ready.`);
  };

  const handleStart = async () => {
    if (!SpeechRecognition || !recognitionRef.current) {
      setStatus("Speech recognition is not available.");
      return;
    }
    try {
      await recognitionRef.current.start();
      listeningRef.current = true;
      setListening(true);
      setStatus("Listening… play the uploaded media or YouTube video nearby.");
      if (mediaRef.current?.paused) {
        try {
          await mediaRef.current.play();
        } catch (error) {
          setStatus("Playback blocked. Interact with the page to allow audio capture.");
        }
      }
    } catch (error) {
      setStatus(`Unable to start recognition: ${error.message}`);
    }
  };

  const handleStop = () => {
    listeningRef.current = false;
    recognitionRef.current?.stop();
    setListening(false);
    setStatus("Transcription stopped.");
  };

  const handleClear = () => {
    transcriptRef.current = "";
    setTranscript("");
  };

  const handleCopyTranscript = async () => {
    if (!transcript) return;
    try {
      await navigator.clipboard.writeText(transcript);
      setStatus("Transcript copied to clipboard.");
    } catch (error) {
      setStatus("Copy failed.");
    }
  };

  const handleDownloadTranscript = () => {
    if (!transcript) return;
    const downloadName = `${(uploadedMediaName || "transcript").replace(/\\.[^.]+$/, "")}-transcript.txt`;
    const anchor = document.createElement("a");
    anchor.href = `data:text/plain;charset=utf-8,${encodeURIComponent(transcript)}`;
    anchor.download = downloadName;
    anchor.click();
    setStatus("Transcript downloaded.");
  };

  return (
    <section className="studio-panel transcription-panel">
      <div className="studio-panel__dual-grid transcription__layout">
        <div className="studio-panel__controls-column">
          <div className="studio-panel__header">
            <h2>Transcription Studio</h2>
            <p>
              Upload media or drop a YouTube URL, then start the speech recognizer and capture the audio through your mic.
            </p>
          </div>
          <div className="studio-panel__control-row">
            <label className="studio-panel__file-control">
              <span>Upload media</span>
              <input type="file" accept="audio/*,video/*" onChange={handleMediaUpload} />
            </label>
            <span className="studio-panel__status">
              {uploadedMediaName || "No media selected."}
            </span>
          </div>
          <label>
            <span>YouTube link (optional)</span>
            <input
              type="url"
              value={youtubeUrl}
              onChange={(event) => setYoutubeUrl(event.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </label>
          <div className="studio-panel__transcription-actions">
            <button
              type="button"
              className="studio-panel__cta"
              onClick={handleStart}
              disabled={!SpeechRecognition || listening}
            >
              Start
            </button>
            <button
              type="button"
              className="studio-panel__cta studio-panel__cta--secondary"
              onClick={handleStop}
              disabled={!SpeechRecognition || !listening}
            >
              Stop
            </button>
            <button
              type="button"
              className="studio-panel__cta studio-panel__cta--secondary"
              onClick={handleClear}
              disabled={!transcript}
            >
              Clear
            </button>
            <button
              type="button"
              className="studio-panel__cta studio-panel__cta--secondary"
              onClick={handleCopyTranscript}
              disabled={!transcript}
            >
              Copy
            </button>
            <button
              type="button"
              className="studio-panel__cta studio-panel__cta--secondary"
              onClick={handleDownloadTranscript}
              disabled={!transcript}
            >
              Download
            </button>
          </div>
        </div>
        <div className="studio-panel__preview-column">
          {mediaPreviewUrl && (
            <>
              {mediaType === "video" ? (
                <video ref={mediaRef} src={mediaPreviewUrl} controls className="studio-panel__video-preview" />
              ) : (
                <audio ref={mediaRef} src={mediaPreviewUrl} controls className="studio-panel__audio-preview" />
              )}
            </>
          )}
          {youtubeVideoId && (
            <YouTube
              videoId={youtubeVideoId}
              opts={{ width: "100%", height: "200" }}
              className="studio-panel__youtube-preview"
            />
          )}
          <p className="studio-panel__helper">Status: {status}</p>
          <textarea
            className="studio-panel__form-textarea"
            value={transcript}
            readOnly
            placeholder="Transcribed text shows up here. Make sure your media audio is audible and the microphone is enabled."
            rows={6}
          />
        </div>
      </div>
    </section>
  );
};

export default TranscriptionTool;
